export type LifeStage = 'embryonic' | 'awakening' | 'perception' | 'resonance'

export interface LifeDimensions {
  energy: number
  curiosity: number
  connection: number
  memory: number
  balance: number
}

export interface LifeState {
  version: number
  ageDays: number
  stage: LifeStage
  dimensions: LifeDimensions
  updatedAt: string
}

export interface InteractionMetrics {
  gentleMs: number
  activeBursts: number
  interactionKinds: number
  presenceMs: number
}

export interface InteractionSubmission {
  visitorToken: string
  windowStartedAt: string
  metrics: InteractionMetrics
}

const stages = new Set<LifeStage>(['embryonic', 'awakening', 'perception', 'resonance'])
const dimensionKeys: (keyof LifeDimensions)[] = [
  'energy', 'curiosity', 'connection', 'memory', 'balance',
]

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function parseLifeState(value: unknown): LifeState {
  if (!isRecord(value) || !isRecord(value.dimensions)) {
    throw new Error('invalid_life_state')
  }

  const dimensions = value.dimensions
  const dimensionsValid = dimensionKeys.every((key) => {
    const number = dimensions[key]
    return typeof number === 'number' && Number.isFinite(number) && number >= 0 && number <= 100
  })
  const versionValid = Number.isInteger(value.version) && (value.version as number) >= 0
  const ageDaysValid = Number.isInteger(value.ageDays) && (value.ageDays as number) >= 0
  const stageValid = typeof value.stage === 'string' && stages.has(value.stage as LifeStage)
  const updatedAtValid = typeof value.updatedAt === 'string' && !Number.isNaN(Date.parse(value.updatedAt))

  if (!versionValid || !ageDaysValid || !stageValid || !dimensionsValid || !updatedAtValid) {
    throw new Error('invalid_life_state')
  }

  return value as unknown as LifeState
}
